import { LitElement, css, html } from 'lit'
import { customElement, state } from 'lit/decorators.js'
import { evaluate } from '../lib/recommendation.js'
import { DEFAULT_STATE, debounce, loadState, saveState } from '../lib/storage.js'
import type { AppState } from '../lib/types.js'
import {
  ACCURACY_WARN_M,
  WeatherError,
  currentPosition,
  fetchOutdoor,
  formatAccuracy,
  formatAge,
} from '../lib/weather.js'
import { tokens } from '../styles/tokens.js'
import type { StationChangeDetail } from './kil-station.js'
import './kil-station.js'
import './kil-verdict.js'

type WeatherStatus =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | {
      kind: 'ok'
      observedAt: number
      stale: boolean
      latitude: number
      longitude: number
      accuracyM: number
    }
  | { kind: 'error'; message: string }

@customElement('kil-app')
export class KilApp extends LitElement {
  static override styles = [
    tokens,
    css`
      :host {
        display: block;
        max-width: 62rem;
        margin: 0 auto;
        padding: 1.25rem 1rem 4rem;
      }

      header.masthead {
        display: flex;
        align-items: baseline;
        justify-content: space-between;
        flex-wrap: wrap;
        gap: 0.5rem;
        padding-bottom: 1.25rem;
      }

      .wordmark {
        font-size: 0.9375rem;
        font-weight: 700;
        font-stretch: 118%;
        letter-spacing: 0.01em;
      }

      .stations {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(17rem, 1fr));
        gap: 1rem;
        margin-top: 1rem;
      }

      button {
        font: inherit;
        font-size: 0.8125rem;
        font-weight: 600;
        color: var(--kil-surface);
        background: var(--kil-ink);
        border: 1px solid var(--kil-ink);
        border-radius: 999px;
        padding: 0.45rem 0.9rem;
        cursor: pointer;
        transition: opacity var(--kil-motion, 160ms) ease;
      }

      button:hover:not(:disabled) {
        opacity: 0.85;
      }

      button:disabled {
        cursor: progress;
        opacity: 0.6;
      }

      button:focus-visible {
        outline: 2px solid var(--kil-accent);
        outline-offset: 2px;
      }

      .hint {
        margin: 0;
        font-size: 0.75rem;
        color: var(--kil-ink-dim);
        flex-basis: 100%;
      }

      .hint.error {
        color: var(--kil-bad);
      }

      .settings {
        margin-top: 1rem;
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 0.75rem 1.25rem;
        background: var(--kil-surface);
        border: 1px solid var(--kil-line);
        border-radius: var(--kil-radius);
        padding: 1rem 1.25rem;
      }

      .settings label {
        font-size: 0.6875rem;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: var(--kil-ink-dim);
      }

      .settings input[type='range'] {
        flex: 1 1 10rem;
        accent-color: var(--kil-accent);
      }

      .settings .readout {
        font-family: var(--kil-font-mono);
        font-variant-numeric: tabular-nums;
        font-size: 0.9375rem;
      }

      .settings p {
        margin: 0;
        flex-basis: 100%;
        font-size: 0.75rem;
        color: var(--kil-ink-dim);
      }

      .reserve {
        flex-basis: 100%;
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(9rem, 1fr));
        gap: 0.9rem 1.5rem;
        margin: 0;
        padding-top: 0.9rem;
        border-top: 1px dashed var(--kil-line);
      }

      .reserve.critical {
        color: var(--kil-bad);
      }

      footer {
        margin-top: 2rem;
        font-size: 0.75rem;
        line-height: 1.6;
        color: var(--kil-ink-dim);
      }

      footer p {
        margin: 0 0 0.6rem;
      }

      footer a {
        color: inherit;
      }

      .disclaimer {
        margin-top: 1.25rem;
        padding: 1rem 1.25rem;
        border: 1px solid var(--kil-line);
        border-left: 3px solid var(--kil-line-strong);
        border-radius: var(--kil-radius);
        background: var(--kil-surface);
      }

      .disclaimer h2 {
        margin: 0 0 0.5rem;
        font-size: 0.6875rem;
        font-weight: 700;
        letter-spacing: 0.14em;
        text-transform: uppercase;
        font-stretch: 112%;
        color: var(--kil-ink);
      }

      .disclaimer p:last-child {
        margin-bottom: 0;
      }
    `,
  ]

  @state() private appState: AppState = DEFAULT_STATE
  @state() private weather: WeatherStatus = { kind: 'idle' }

  private persist = debounce((next: AppState) => saveState(next), 400)

  override connectedCallback(): void {
    super.connectedCallback()
    this.appState = loadState()
  }

  private patch(next: Partial<AppState>): void {
    this.appState = { ...this.appState, ...next }
    this.persist(this.appState)
  }

  private onIndoorChange = (event: CustomEvent<StationChangeDetail>): void => {
    this.patch({ indoor: { t: event.detail.t, rh: event.detail.rh } })
  }

  private onOutdoorChange = (event: CustomEvent<StationChangeDetail>): void => {
    this.weather = { kind: 'idle' }
    this.patch({ outdoor: { t: event.detail.t, rh: event.detail.rh } })
  }

  /** Siehe kil-station: Mausrad darf den Regler nicht verstellen. */
  private onWheel = (event: WheelEvent): void => {
    event.preventDefault()
    window.scrollBy({ top: event.deltaY, behavior: 'instant' })
  }

  private onOffsetChange = (event: Event): void => {
    this.patch({ surfaceOffsetK: Number((event.target as HTMLInputElement).value) })
  }

  private loadWeather = async (): Promise<void> => {
    this.weather = { kind: 'loading' }
    try {
      const position = await currentPosition()
      const reading = await fetchOutdoor(position.latitude, position.longitude)
      this.patch({ outdoor: { t: reading.t, rh: reading.rh } })
      this.weather = {
        kind: 'ok',
        observedAt: reading.observedAt,
        stale: reading.stale,
        latitude: reading.latitude,
        longitude: reading.longitude,
        accuracyM: position.accuracyM,
      }
    } catch (error) {
      this.weather = {
        kind: 'error',
        message:
          error instanceof WeatherError ? error.message : 'Abruf fehlgeschlagen. Bitte manuell eintragen.',
      }
    }
  }

  private get outdoorSource(): string {
    if (this.weather.kind !== 'ok') return ''
    // Das Alter bezieht sich auf den Messzeitpunkt des Dienstes, nicht auf den Abruf.
    const age = formatAge(this.weather.observedAt)
    return this.weather.stale ? `Wetterdienst, ${age} (offline)` : `Wetterdienst, ${age}`
  }

  private fmt(value: number, digits = 1): string {
    return Number.isFinite(value) ? value.toFixed(digits).replace('.', ',') : '–'
  }

  /** Herkunft und Verlässlichkeit der abgerufenen Außenwerte offenlegen. */
  private renderWeatherHint() {
    const status = this.weather

    if (status.kind === 'error') {
      return html`<p class="hint error">${status.message}</p>`
    }

    if (status.kind !== 'ok') {
      return html`<p class="hint">
        Vom Wetterdienst für deinen Standort – der eigene Außenfühler bleibt genauer.
      </p>`
    }

    const place = `${status.latitude.toFixed(2)}°N ${status.longitude.toFixed(2)}°O`
    const vague = status.accuracyM > ACCURACY_WARN_M

    return html`
      <p class="hint ${vague ? 'error' : ''}">
        Modellwert für ${place} (Standort ${formatAccuracy(status.accuracyM)}).
        ${vague
          ? 'Der Standort ist so grob bestimmt, dass das eine ganz andere Wetterlage sein kann – am Rechner kommt er meist aus dem WLAN, nicht vom GPS. Werte prüfen.'
          : 'Ein Modellwert aus dem Gitter, keine Messung an deiner Hauswand.'}
      </p>
    `
  }

  override render() {
    const recommendation = evaluate(this.appState)
    const accent = `var(--kil-verdict-${recommendation.verdict})`
    const surfaceT = this.appState.indoor.t - this.appState.surfaceOffsetK
    // Abstand der kältesten Oberfläche zum Außentaupunkt: solange positiv, kondensiert nichts.
    const reserve = surfaceT - recommendation.outdoor.dewPoint

    return html`
      <header class="masthead">
        <span class="wordmark">Kann ich lüften?</span>
        <span class="eyebrow">Innen- und Außenwerte vergleichen</span>
      </header>

      <main style="--kil-accent:${accent}">
        <kil-verdict .recommendation=${recommendation}></kil-verdict>

        <div class="stations">
          <kil-station
            heading="Innen"
            .t=${this.appState.indoor.t}
            .rh=${this.appState.indoor.rh}
            .dewPoint=${recommendation.indoor.dewPoint}
            .absolute=${recommendation.indoor.absolute}
            @station-change=${this.onIndoorChange}
          ></kil-station>

          <kil-station
            heading="Außen"
            .t=${this.appState.outdoor.t}
            .rh=${this.appState.outdoor.rh}
            .dewPoint=${recommendation.outdoor.dewPoint}
            .absolute=${recommendation.outdoor.absolute}
            source=${this.outdoorSource}
            @station-change=${this.onOutdoorChange}
          >
            <button
              type="button"
              ?disabled=${this.weather.kind === 'loading'}
              @click=${this.loadWeather}
            >
              ${this.weather.kind === 'loading' ? 'Hole Wetterdaten …' : 'Werte per Standort holen'}
            </button>
            ${this.renderWeatherHint()}
          </kil-station>
        </div>

        <div class="settings">
          <label for="offset">Kälteste Oberfläche im Raum</label>
          <input
            id="offset"
            type="range"
            min="0"
            max="8"
            step="0.5"
            .value=${String(this.appState.surfaceOffsetK)}
            @input=${this.onOffsetChange}
            @wheel=${this.onWheel}
          />
          <span class="readout"
            >${String(this.appState.surfaceOffsetK).replace('.', ',')}&thinsp;K kälter als die
            Raumluft</span
          >

          <dl class="reserve ${reserve <= 0 ? 'critical' : ''}">
            <kil-metric
              label="Kälteste Oberfläche"
              value=${this.fmt(surfaceT)}
              unit="°C"
            ></kil-metric>
            <kil-metric
              label="Taupunkt außen"
              value=${this.fmt(recommendation.outdoor.dewPoint)}
              unit="°C"
            ></kil-metric>
            <kil-metric
              label=${reserve > 0 ? 'Reserve bis Kondensation' : 'Unterschritten um'}
              value=${this.fmt(Math.abs(reserve))}
              unit="K"
            ></kil-metric>
          </dl>

          <p>
            Wärmebrücken, Außenwandecken und Fensterlaibungen sind kälter als die Raumluft. Sinkt
            die Reserve auf null, schlägt sich die hereingeholte Luft dort als Wasser nieder.
            0–1 K für den erdberührten Keller, 3 K als Standard, 5 K und mehr im Altbau mit
            bekannten kalten Ecken.
          </p>
        </div>
      </main>

      <footer>
        <p>
          Vergleicht die absolute Feuchte beider Luftmassen nach der Magnus-Formel und prüft, ob
          die Außenluft an kalten Oberflächen kondensieren würde. Rechenweg, Regeln und Quellen
          liegen im Ordner <code>docs/</code>.
        </p>

        <section class="disclaimer">
          <h2>Haftungsausschluss</h2>
          <p>
            Diese Seite ist ein privates Projekt und liefert eine rechnerische Orientierungshilfe
            aus den Werten, die eingegeben oder von einem Wetterdienst abgerufen werden. Sie
            ersetzt weder eine fachgerechte Messung noch die Beurteilung durch eine sachkundige
            Person.
          </p>
          <p>
            Die Berechnung beruht auf vereinfachten Modellen und auf Werten, deren Richtigkeit
            hier nicht überprüfbar ist: Fühler können falsch aufgestellt oder dejustiert sein,
            Wetterdaten sind Modellwerte für einen Gitterpunkt, und die Temperatur der kältesten
            Oberfläche im Raum wird lediglich geschätzt. Die Ergebnisse können deshalb im
            Einzelfall falsch sein.
          </p>
          <p>
            Es wird keine Gewähr für Richtigkeit, Vollständigkeit, Aktualität oder Verfügbarkeit
            übernommen. Die Nutzung erfolgt auf eigene Verantwortung. Eine Haftung für Schäden,
            die aus der Nutzung oder Nichtnutzung dieser Seite oder aus dem Vertrauen auf ihre
            Ergebnisse entstehen, ist ausgeschlossen – insbesondere für Feuchte-, Schimmel- und
            sonstige Bauschäden, für Energiekosten sowie für gesundheitliche Folgen.
          </p>
          <p>
            Unberührt bleibt die Haftung für Vorsatz und grobe Fahrlässigkeit sowie für Schäden
            aus der Verletzung des Lebens, des Körpers oder der Gesundheit.
          </p>
          <p>
            Bei sichtbarem Schimmel, durchfeuchteten Wänden oder dem Verdacht auf einen Bauschaden
            ist eine Fachperson hinzuzuziehen – etwa ein Sachverständigenbüro für Bauphysik, ein
            Fachbetrieb oder eine Energieberatung.
          </p>
        </section>
      </footer>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'kil-app': KilApp
  }
}
